// src/components/customers/tabs/tabs-Addresses.jsx
import React from "react";
import {
  useCustomerAddressesQuery,
  useCreateAddressMutation,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} from "@queries/customers/addresses";
import { formatCoordinatePair, parseCoordinatePair } from "@lib/coordinates";
import { loadGoogleMaps } from "@lib/googleMapsLoader";

const MapPicker = React.lazy(() => import("@components/maps/MapPicker"));

const AMMAN_AREAS = [
  "الاندلسية", "البنيات", "البيادر", "الجبيهة", "الجندويل", "دير غبار", "الرابية",
  "ضاحية الرشيد", "الرضوان", "الروابي", "السهل", "الشميساني", "الصحابة", "الصويفية",
  "الظهير", "العبدلي", "الكرسي", "الكمالية", "المدينة الرياضية", "المقابلين", "الياسمين",
  "ام اذينة", "ام السماق", "تلاع العلي", "جبل الحسين", "جبل عمان", "خلدا", "دابوق",
  "شفا بدران", "صويلح", "طبربور", "طريق المطار", "عبدون", "عرجان", "ماحص", "مرج الحمام",
  "ناعور", "عين الباشا", "حي نزال", "جبل اللويبدة", "جبل التاج", "جبل الزهور", "الصناعة",
  "الجاردنز", "الجبل الاخضر", "ابو نصير", "ابوعلندا", "جبل النصر", "القسطل", "جبل النزهة",
  "السلط", "القويسمة"
].sort();

export default function AddressesTab({ customer_id, customer }) {
  const { data, isLoading } = useCustomerAddressesQuery(customer_id);
  const createMut = useCreateAddressMutation(customer_id);
  const updateMut = useUpdateAddressMutation(customer_id);
  const deleteMut = useDeleteAddressMutation(customer_id);

  const buildEmptyForm = React.useCallback(() => ({
    name: customer?.name || "",
    telephone: customer?.telephone || customer?.alt_mobile || "",
    address: "",
    area: "",
    country_id: 108, // Jordan
    city_id: 1704,   // Amman
    type: "home",
    comment: "",
    lat: "",
    lng: "",
    building_number: "",
    floor: "",
  }), [customer?.alt_mobile, customer?.name, customer?.telephone]);

  const [gmReady, setGmReady] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(() => buildEmptyForm());
  const [mapKey, setMapKey] = React.useState(Date.now());
  const [coordinateInput, setCoordinateInput] = React.useState("");

  React.useEffect(() => {
    let ok = true;
    loadGoogleMaps().then(() => ok && setGmReady(true));
    return () => {
      ok = false;
    };
  }, []);

  React.useEffect(() => {
    if (editing) {
      setForm({
        address_id: editing.address_id,
        name: editing.name || "",
        telephone: editing.telephone || "",
        address: editing.address || "",
        area: editing.area || "",
        country_id: editing.country_id ?? 108,
        city_id: editing.city_id ?? 1704,
        type: editing.type || "home",
        comment: editing.comment || "",
        lat: editing.lat ?? "",
        lng: editing.lng ?? "",
        building_number: editing.building_number || "",
        floor: editing.floor || "",
      });
    } else {
      setForm(buildEmptyForm());
    }
    setMapKey(Date.now());
  }, [buildEmptyForm, editing]);

  React.useEffect(() => {
    setCoordinateInput(formatCoordinatePair(form.lat, form.lng));
  }, [form.lat, form.lng]);

  const handleCoordinateInputChange = (value) => {
    setCoordinateInput(value);

    if (value.trim() === "") {
      setForm((current) => ({ ...current, lat: "", lng: "" }));
      return;
    }

    const parsed = parseCoordinatePair(value);

    if (parsed) {
      setForm((current) => ({ ...current, lat: parsed.lat, lng: parsed.lng }));
      
      if (!form.address || form.address.trim() === "" || form.address.trim().toLowerCase() === "amman") {
        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: Number(parsed.lat), lng: Number(parsed.lng) } }, (results, status) => {
            setForm((curr) => {
              if (!curr.address || curr.address.trim() === "" || curr.address.trim().toLowerCase() === "amman") {
                const newAddr = (status === "OK" && results?.[0]) ? results[0].formatted_address : "Amman";
                return { ...curr, address: newAddr };
              }
              return curr;
            });
          });
        } else {
          setForm((curr) => {
            if (!curr.address || curr.address.trim() === "" || curr.address.trim().toLowerCase() === "amman") {
              return { ...curr, address: "Amman" };
            }
            return curr;
          });
        }
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    
    // Ensure comment is at least a space if empty, to satisfy API string requirement
    const payload = { ...form };
    if (!payload.comment || payload.comment.trim() === "") {
      payload.comment = " ";
    }
    if (payload.lat == null || payload.lat === "") payload.lat = "";
    if (payload.lng == null || payload.lng === "") payload.lng = "";
    
    try {
      if (payload.address_id) {
        await updateMut.mutateAsync(payload);
      } else {
        await createMut.mutateAsync(payload);
      }
      setEditing(null);
      setForm(buildEmptyForm());
    } catch (err) {
      console.error("Failed to save address", err);
      // The error is likely caught and handled by the mutation, 
      // but we can add a basic alert if needed or rely on global error handling
      if (err?.response?.data?.message) {
        alert(`Error: ${err.response.data.message}`);
      } else if (err?.message) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  const rows = Array.isArray(data?.data) ? data.data : [];

  return (
    <>
      <div className="title">Addresses</div>

      {isLoading && <div>Loading…</div>}

      {!isLoading && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button className="oc-btn" type="button" onClick={() => {
              setEditing(null);
              setForm(buildEmptyForm());
              setMapKey(Date.now());
            }}>
              Add New Address
            </button>
          </div>

          <table className="oc-table">
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Telephone</th><th>Address</th><th>Area</th><th>Type</th><th>Coordinates</th><th>Map</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const hasCoords = r.lat != null && r.lng != null && r.lat !== "" && r.lng !== "";
                const coordStr = hasCoords ? `${r.lat},${r.lng}` : "";
                const mapUrl = hasCoords ? `https://maps.google.com/?q=${r.lat},${r.lng}` : null;
                return (
                  <tr key={r.address_id}>
                    <td>{r.address_id}</td>
                    <td>{r.name}</td>
                    <td>{r.telephone}</td>
                    <td>
                      {r.address}
                      {(r.building_number || r.floor) && (
                        <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                          {r.building_number && `Building Number: ${r.building_number}`}
                          {r.building_number && r.floor && " | "}
                          {r.floor && `Floor: ${r.floor}`}
                        </div>
                      )}
                    </td>
                    <td>{r.area}</td>
                    <td>{r.type}</td>
                    <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{coordStr}</td>
                    <td>
                      {mapUrl && (
                        <a href={mapUrl} target="_blank" rel="noreferrer" style={{ color: "#1a73e8", textDecoration: "none" }}>
                          View
                        </a>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="oc-btn" onClick={() => setEditing(r)} type="button">Edit</button>
                      <button className="oc-btn danger" onClick={() => deleteMut.mutate(r.address_id)} type="button">Delete</button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", color: "#666" }}>No addresses</td></tr>
              )}
            </tbody>
          </table>

          <form className="oc-form" onSubmit={submit} style={{ marginTop: 16 }}>
            <div className="title" style={{ marginBottom: 8 }}>{form.address_id ? "Edit Address" : "New Address"}</div>
            <div className="oc-grid 2cols">
              <div className="oc-field">
                <label>Customer Name</label>
                <input className="oc-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="oc-field">
                <label>Mobile</label>
                <input className="oc-input" value={form.telephone} onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))} required />
              </div>
              <div className="oc-field" style={{ gridColumn: "1 / -1" }}>
                <label>Map</label>
                <React.Suspense fallback={<div>Loading map…</div>}>
                  {gmReady ? (
                    <MapPicker
                      key={mapKey}
                      value={{ lat: form.lat, lng: form.lng }}
                      onChange={(p) => {
                        setForm((curr) => ({ ...curr, ...p }));
                        
                        if (!form.address || form.address.trim() === "" || form.address.trim().toLowerCase() === "amman") {
                          if (window.google?.maps?.Geocoder) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode({ location: p }, (results, status) => {
                              setForm((curr) => {
                                if (!curr.address || curr.address.trim() === "" || curr.address.trim().toLowerCase() === "amman") {
                                  const newAddr = (status === "OK" && results?.[0]) ? results[0].formatted_address : "Amman";
                                  return { ...curr, address: newAddr };
                                }
                                return curr;
                              });
                            });
                          } else {
                            setForm((curr) => {
                              if (!curr.address || curr.address.trim() === "" || curr.address.trim().toLowerCase() === "amman") {
                                return { ...curr, address: "Amman" };
                              }
                              return curr;
                            });
                          }
                        }
                      }}
                      height={400}
                    />
                  ) : (
                    <div style={{ height: 400, display: "grid", placeItems: "center", background: "#f5f5f5" }}>Loading map…</div>
                  )}
                </React.Suspense>
              </div>
              <div className="oc-field" style={{ gridColumn: "1 / -1" }}>
                <label>Coordinates (lat,lng)</label>
                <input
                  className="oc-input"
                  value={coordinateInput}
                  onChange={(e) => handleCoordinateInputChange(e.target.value)}
                  placeholder="31.9539,35.9106"
                />
              </div>
              <div className="oc-field">
                <label>Address</label>
                <input className="oc-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} required />
              </div>
              <div className="oc-field">
                <label>Building number</label>
                <input className="oc-input" value={form.building_number} onChange={(e) => setForm((f) => ({ ...f, building_number: e.target.value }))} />
              </div>
              <div className="oc-field">
                <label>Floor</label>
                <input className="oc-input" value={form.floor} onChange={(e) => setForm((f) => ({ ...f, floor: e.target.value }))} />
              </div>
              <div className="oc-field">
                <label>Country</label>
                <select className="oc-input" value={form.country_id} onChange={(e) => setForm((f) => ({ ...f, country_id: Number(e.target.value) }))}>
                  <option value={108}>Jordan</option>
                  {/* Add other countries here if needed */}
                </select>
              </div>
              <div className="oc-field">
                <label>City</label>
                <select className="oc-input" value={form.city_id} onChange={(e) => setForm((f) => ({ ...f, city_id: Number(e.target.value) }))}>
                  <option value={1704}>Amman</option>
                  {/* Add other cities here if needed */}
                </select>
              </div>
              <div className="oc-field">
                <label>Area</label>
                <select className="oc-input" value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} required>
                  <option value="" disabled>Select Area</option>
                  {AMMAN_AREAS.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div className="oc-field">
                <label>Type</label>
                <select className="oc-input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                  <option value="home">Home</option>
                  <option value="work">Work</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="oc-field" style={{ gridColumn: "1 / -1" }}>
                <label>Comment</label>
                <textarea className="oc-input" rows={4} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <button className="oc-btn primary" type="submit">{form.address_id ? "Update Address" : "Create Address"}</button>
              <button className="oc-btn" type="button" onClick={() => { setEditing(null); setForm(buildEmptyForm()); setMapKey(Date.now()); }}>Reset</button>
            </div>
          </form>
        </>
      )}
    </>
  );
}

